<template>
	<div
		class=""
		:class="{ loading, unavailable: unavailable || product.numberOfStoresWithProduct < 1 }"
	>
		<TnParagraph>
			<TnHeading
				type="h2"
				size="xs"
				bold
				class="margin-bottom-s"
				responsive
				>Klikk & Hent hos Telenorbutikken
			</TnHeading>
		</TnParagraph>
		<form
			v-if="!loading"
			class="search"
		>
			<div class="flex flex-column">
				<TnInputText
					:placeholder="placeholder"
					v-model="query"
					:disabled="loading"
					class="search-input"
					iconLeft="location"
					@enter="handlePostnumberSearch"
					max="4"
					hide-details
					:loading="loading"
					type="text"
					size="s"
				/>

				<div class="flex items-top justify-center">
					<TnButton
						icon-left="near-me-off"
						text="Butikk nær meg"
						@click="searchByLocation"
						size="s"
						secondary
					>
					</TnButton>

					<TnLink
						size="s"
						class="margin-left-m icon"
						@click="toggleClickAndCollectModal()"
					>
						Se alle butikker
					</TnLink>
				</div>
			</div>
		</form>
		<TnParagraph
			size="s"
			v-if="text"
		>
			<TnIcon
				name="success-circle-filled"
				class="margin-right-s icon"
				v-if="!unavailable && product.numberOfStoresWithProduct > 0"
			></TnIcon>
			<TnIcon
				name="close"
				class="margin-right-s icon"
				v-else
			></TnIcon>
			{{ text }}
		</TnParagraph>
		<template v-else>
			<TnParagraph
				size="s"
				v-if="loading"
			>
				<TnIcon
					name="spinner"
					class="margin-right-s icon spinner"
				/>
				Sjekker klikk&hent lagerstatus...
			</TnParagraph>

			<TnParagraph
				size="s"
				v-else-if="!unavailable && product.numberOfStoresWithProduct > 0"
			>
				<div>
					<div
						v-for="(store, index) in twoClosestStoresRelativeToSearch"
						:key="`attachment-${index}`"
						class="store-card"
					>
						<ECommerceStoreCardHeader
							:store="store"
							:active="active"
						/>
						<EcommmerceClickAndCollectStoreRow
							:store="store"
							@reserve="toggleClickAndCollectModal({ store })"
						/>
					</div>
				</div>
			</TnParagraph>
			<div v-else>
				<TnParagraph size="s">
					<TnIcon
						name="close"
						class="margin-right-s unavailable-icon"
					/>
					Ikke tilgjengelig for Klikk & Hent
				</TnParagraph>
			</div>
		</template>
	</div>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import { mapState as mapPiniaState } from "pinia";
import { useGeolocationStore } from "~/pinia/platform/geolocation/geolocation.ts";
import { todaysClosingTime } from "~/helpers/ecommerce/clickAndCollect/clickAndCollectMessageUtil";

export default defineNuxtComponent({
	name: "StoreStock",

	data() {
		return {
			query: "",
		};
	},
	props: {
		storeStock: {
			type: String,
		},
		text: {
			type: String,
		},
	},

	computed: {
		...mapState("butikken", ["closestStore"]),
		...mapState("ecommerce", ["product", "storeSelected"]),
		...mapPiniaState(useGeolocationStore(), ["position"]),
		unavailable() {
			if (this.product.disableClickAndCollect) return true;
			if (this.storeStock === "Not in stock") return true;
			if (this.storeStock === "In stock") return false;
			if (this.loading) return false;
			return false;
		},
		stores() {
			return this.product.clickAndCollectStores;
		},
		loading() {
			return this.product.loadingClickAndCollect;
		},
		placeholder() {
			return "Postnummer eller sted";
		},
		preferredStoreStatus() {
			if (this.closestStore) {
				return this.stores
					?.filter((store) => store.in_stock > 0)
					.find((store) => store.name.indexOf(this.closestStore.name) === 0)?.stock_text;
			} else {
				return null;
			}
		},
		twoClosestStoresRelativeToSearch() {
			if (!this.product.clickAndCollectStores || this.product.clickAndCollectStores.length == 0) {
				return null;
			}
			return this.product.clickAndCollectStores.slice(0, 2);
		},
	},

	methods: {
		...mapMutations("ecommerce", ["toggleClickAndCollectModal"]),
		...mapActions("ecommerce", ["getNumberOfStoresWithProduct", "getStoresWithProduct"]),
		...mapPiniaState(useGeolocationStore(), ["position"]),
		async handlePostnumberSearch() {
			this.loading = true;
			await this.getStoresWithProduct({ gtin: this.product.gtin, query: this.query, location: null });
			this.loading = false;
		},
		async searchByLocation() {
			this.loading = true;
			this.locationPending = true;

			await useGeolocationStore().requestBestAvailablePosition(false);
			const position = useGeolocationStore().position;
			this.locationPending = false;
			await this.getStoresWithProduct({ gtin: this.product.gtin, query: "", location: position });
			this.loading = false;
		},
		distanceSet(store) {
			return store.distanceFromUser !== null;
		},
		openingHours(store) {
			return todaysClosingTime(store);
		},
	},
});
</script>

<style lang="scss" scoped>
@keyframes rotation {
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(359deg);
	}
}

.spinner {
	animation: rotation 2s infinite linear;
}

.unavailable {
	color: $color-neutrals-600-shade;
}

.icon {
	position: relative;
	top: -4px;
}

.unavailable-icon {
	position: relative;
	top: 5px;
}

.dealer-search {
	margin-top: 16px 0 16px 0;
}

.search-input {
	margin: 16px;
}

.store-card {
	&.active {
		background-color: $color-neutrals-50-tint;
	}
	padding: 16px 0 16px 0;

	.header {
		margin: 8px 0;
	}

	&:not(.active) {
		cursor: pointer;
	}
}
</style>
